import { useState } from 'react'
import gql from 'graphql-tag'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input, FormFeedback, Row, Col, Alert, UncontrolledTooltip } from 'reactstrap'
import { useQuery, useMutation } from '@apollo/client'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import Select from 'react-select'

import toTitleCase from '../../Functions/toTitleCase'
import { PeopleSchema } from './IncidentModalsSchema'
import SearchPlaces from '../../Material/SearchPlaces'
import './Modals.css'
import AddRepresentativeModal from './AddRepresentativeModal'
import { RepresentativeSelect, formatRepresentativeOption } from './RepresentativeSelect'

const CreateIncidentParty = gql`
    mutation createIncidentParty($input: CreateIncidentPartyMutationInput!){
        createIncidentParty(input:$input){
            ok
            errors{
                field
                messages
            }
        }
    }
`

const CreateIncidentPartyPeople = gql`
    mutation createIncidentPartyPeople($input: CreateIncidentPartyPeopleMutationInput!){
        createIncidentPartyPeople(input:$input){
            ok
            errors{
                field
                messages
            }
        }
    }
`

const UpdateIncident = gql`
    mutation updateIncident($input: UpdateIncidentMutationInput!){
        updateIncident(input:$input){
            ok
            errors{
                field
                messages
            }
        }
    }
`

const INJURIES_ENUM_QUERY = gql`
    query GetInjuriesChoices {
        injuriesChoices: __type(name: "IncidentsIncidentPartyPeopleInjuriesChoices") {
            enumValues {
                name
                description
            }
        }
        
    }
`

const ROLES_ENUM_QUERY = gql`
    query GetRolesChoices {
        rolesChoices: __type(name: "IncidentsIncidentPartyPeopleRoleChoices") {
            enumValues {
                name
                description
            }
        }
    }
`

const ALL_DRIVERS_QUERY = gql`
    query AllDrivers($searchTerm: String, $branchIds: [ID]){
        drivers: allDrivers(searchTerm: $searchTerm, branchIds: $branchIds, first:10){
            edges{
                node{
                    id
                    pk
                    name
                    email
                    phone
                    address
                    dmvLicense
                    branch{
                        id
                        name
                    }
                }
            }
        }
    }
`

const AddPeopleModal = ({ isOpen, toggle, partyType, incidentId, partyId, role, roles, refetchQuery, onCreate, isDefaultParty = false, incidentBranchIds = null }) => {
    const [error, setError] = useState(null)
    const [partyName, setPartyName] = useState('')
    const [selectedDriver, setSelectedDriver] = useState(null)
    const [driverSearchTerm, setDriverSearchTerm] = useState('')
    const [showDriverDropdown, setShowDriverDropdown] = useState(false)
    const { data: injuriesData, loading: injuriesLoading } = useQuery(INJURIES_ENUM_QUERY)
    const { data: rolesData, loading: rolesLoading } = useQuery(ROLES_ENUM_QUERY)

    const roleOptions = rolesData?.rolesChoices?.enumValues?.filter(opt => role ? opt.name === role : roles && Array.isArray(roles) ? roles.includes(opt.name) : true).map(opt => ({
        value: opt.name,
        label: opt.description || opt.name,
    })) || []
    const [createIncidentParty] = useMutation(CreateIncidentParty)
    const [createIncidentPartyPeople] = useMutation(CreateIncidentPartyPeople)
    const [updateIncident] = useMutation(UpdateIncident)

    const { handleSubmit, reset, control, setValue, watch, formState: { errors, isSubmitting } } = useForm({
        resolver: yupResolver(PeopleSchema),
        defaultValues: {
            role: role || '',
            name: '',
            phoneNumber: '',
            email: '',
            address: '',
            driverLicense: '',
            injuries: '',
            statement: '',
            isBusinessEntity: false,
            representativeId: null,
        }
    })
    const selectedRole = watch("role")
    const isDefaultVehicleDriver = isDefaultParty && selectedRole === "VEHICLE_DRIVER"
    const driverSearchBranchIds = incidentBranchIds && incidentBranchIds.length > 0 ? incidentBranchIds : null
    const { data: driversData, loading: driversLoading } = useQuery(ALL_DRIVERS_QUERY, {
        variables: { searchTerm: driverSearchTerm || null, branchIds: driverSearchBranchIds },
        skip: !isDefaultVehicleDriver,
        fetchPolicy: 'network-only',
    })

    const [createdRepresentativeOption, setCreatedRepresentativeOption] = useState(null)
    const [isRepresentativeModalOpen, setRepresentativeModalOpen] = useState(false)

    const onSubmit = async (data) => {
        setError(null)
        let input = {}
        if (onCreate) {
            onCreate(data)
            return
        }
        if (!partyId && incidentId && (!partyName || partyName === "" || partyName === null)) {
            setError("Party Title is required")
            return
        }
        const shouldUpdateIncidentDriver = partyId && incidentId && isDefaultParty && data.role === "VEHICLE_DRIVER"
        if (shouldUpdateIncidentDriver && !selectedDriver?.id) {
            setError("Driver is required")
            return
        }
        const peopleData = shouldUpdateIncidentDriver ? {
            ...data,
            name: selectedDriver?.name || "",
            phoneNumber: selectedDriver?.phone || "",
            email: selectedDriver?.email || "",
            driverLicense: selectedDriver?.dmvLicense || "",
            address: selectedDriver?.address || "",
        } : data
        if (!partyId && incidentId) {
            input = {
                incidentId,
                partyType,
                partyName: partyName,
                partyPeople: [{ ...peopleData, representativeId: data.representativeId }],
            }
        } else if (partyId) {
            input = { ...peopleData, incidentPartyId: partyId, representativeId: data.representativeId }
        }

        try {
            if (shouldUpdateIncidentDriver) {
                const incidentRes = await updateIncident({ variables: { input: { incidentId, driverId: selectedDriver.id } } })
                if (!incidentRes.data?.updateIncident?.ok) {
                    setError(incidentRes.data?.updateIncident?.errors?.[0]?.messages?.[0] || incidentRes.data?.updateIncident?.errors || "Could not update incident driver")
                    return
                }
            }
            let res = partyId
                ? await createIncidentPartyPeople({ variables: { input } })
                : await createIncidentParty({ variables: { input } })
            if (!partyId && incidentId) {
                if (res.data?.createIncidentParty?.ok) {
                    if (refetchQuery) refetchQuery()
                    reset()
                    setSelectedDriver(null)
                    setDriverSearchTerm('')
                    setShowDriverDropdown(false)
                    toggle()
                } else if (res.data?.createIncidentParty?.errors[0].messages[0]) {
                    setError(res.data?.createIncidentParty?.errors[0].messages[0])
                } else {
                    setError(res.data?.createIncidentParty?.errors)
                }
            } else if (res.data?.createIncidentPartyPeople?.ok) {
                if (refetchQuery) refetchQuery()
                reset()
                setPartyName('')
                setSelectedDriver(null)
                setDriverSearchTerm('')
                setShowDriverDropdown(false)
                toggle()
            } else if (res.data?.createIncidentPartyPeople?.errors[0].messages[0]) {
                setError(res.data?.createIncidentPartyPeople?.errors[0].messages[0])
            } else {
                setError(res.data?.createIncidentPartyPeople?.errors)
            }
        } catch (err) {
            setError('An unexpected error occurred. ' + err.message)
        }
    }

    const injuryOptions = injuriesData?.injuriesChoices?.enumValues?.map(opt => ({
        value: opt.name,
        label: opt.description || opt.name
    })) || []

    const driverSuggestions = driversData?.drivers?.edges || []

    const closeAndReset = () => {
        reset()
        setPartyName('')
        setSelectedDriver(null)
        setDriverSearchTerm('')
        setShowDriverDropdown(false)
        toggle()
    }

    const toggleRepresentativeModal = () => setRepresentativeModalOpen(!isRepresentativeModalOpen)

    const handleNewRepresentative = (newRepresentative) => {
        const newOption = formatRepresentativeOption(newRepresentative)
        setCreatedRepresentativeOption(newOption)
        setValue("representativeId", newOption.value)
    }

    const clearSelectedDriver = () => {
        setSelectedDriver(null)
        setDriverSearchTerm('')
        setShowDriverDropdown(false)
        setValue("name", "")
        setValue("phoneNumber", "")
        setValue("email", "")
        setValue("driverLicense", "")
        setValue("address", "")
    }

    const handleDriverSelect = (driver) => {
        setSelectedDriver(driver || null)
        setDriverSearchTerm(driver?.name || "")
        setShowDriverDropdown(false)
        setValue("name", driver?.name || "")
        setValue("phoneNumber", driver?.phone || "")
        setValue("email", driver?.email || "")
        setValue("driverLicense", driver?.dmvLicense || "")
        setValue("address", driver?.address || "")
    }

    const handleDriverSearchInput = (event) => {
        setDriverSearchTerm(event.target.value)
        setSelectedDriver(null)
        setShowDriverDropdown(true)
        setValue("name", "")
        setValue("phoneNumber", "")
        setValue("email", "")
        setValue("driverLicense", "")
        setValue("address", "")
    }

    return (
        <>
            <Modal className='add-people-modal modal-dialog-centered' isOpen={isOpen} toggle={closeAndReset} size="lg">
                <ModalHeader>Add {toTitleCase(roles && Array.isArray(roles) ? roles[0] : role || partyType)}</ModalHeader>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <ModalBody>
                        {error && <Row><Col><Alert color="danger">{error}</Alert></Col></Row>}
                        {!partyId &&
                            <Row><Col>
                                <FormGroup>
                                    <Label for="partyName">Party Title*</Label>
                                    <Input
                                        id="partyName"
                                        type="text"
                                        placeholder={(partyType === "WITNESS" || role === "WITNESS") ? "e.g., John Doe Witnesses, ABC Store Employees, etc." : (partyType === "OTHER" || role === "OTHER") ? "e.g., Property, Local Bike Club, etc." : (partyType === "CYCLIST" || role === "CYCLIST") ? "e.g., John Doe Cyclists, Local Bike Club, etc." : (partyType === "NON_MOTORIST" || role === "NON_MOTORIST") ? "e.g., Pedestrian, Bicyclist, etc." : (partyType === "UNAUTHORIZED_DRIVER" || role === "UNAUTHORIZED_DRIVER") ? "e.g., Joyride Driver, Stolen Vehicle Driver, etc." : (partyType === "VEHICLE_DRIVER" || role === "VEHICLE_DRIVER") ? "e.g., John Doe, Jane Smith, etc." : ""}
                                        value={partyName}
                                        onChange={(e) => { setPartyName(e.target.value) }}
                                        invalid={!partyName || partyName === "" || partyName === null}
                                    />
                                </FormGroup>
                            </Col></Row>}
                        <Row>
                            <Col>
                                <FormGroup>
                                    <Label for="role">Role*</Label>
                                    <Controller
                                        name="role"
                                        control={control}
                                        render={({ field }) => (
                                            <>
                                                <Select
                                                    className={`bos-custom-select ${errors.role ? 'is-invalid' : ''}`}
                                                    classNamePrefix="bos-select"
                                                    {...field}
                                                    options={roleOptions}
                                                    isLoading={rolesLoading}
                                                    isClearable
                                                    placeholder="Select role"
                                                    onChange={(option) => {
                                                        const roleValue = option ? option.value : ''
                                                        field.onChange(roleValue)
                                                        if (roleValue !== "VEHICLE_DRIVER") {
                                                            clearSelectedDriver()
                                                        }
                                                    }}
                                                    value={roleOptions.find(opt => opt.value === field.value) || null}
                                                />
                                                {errors.role && (
                                                    <div className="invalid-feedback d-block">
                                                        {errors.role.message}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        {isDefaultVehicleDriver &&
                            <Row>
                                <Col>
                                    <FormGroup>
                                        <Label for="driverSearch">Select Driver</Label>
                                        <Input
                                            type="text"
                                            value={driverSearchTerm}
                                            onChange={handleDriverSearchInput}
                                            onFocus={() => setShowDriverDropdown(true)}
                                            placeholder="Search by driver name, tlc, phone, email"
                                            autoComplete="off"
                                        />
                                        {showDriverDropdown && (
                                            <div className="option-list car-search-dropdown">
                                                {driversLoading ? (
                                                    <p className="p-2 mb-0 text-center text-muted">Loading drivers, please wait...</p>
                                                ) : driverSuggestions.length > 0 ? (
                                                    driverSuggestions.map((driver, idx) => (
                                                        <Row key={idx} className="option py-1" style={{ cursor: 'pointer' }} onClick={() => handleDriverSelect(driver.node)}>
                                                            <Col>
                                                                <p className="mb-0" style={{ fontWeight: 500 }}>
                                                                    {driver.node.name || `Driver #${driver.node.pk || ""}`}<br />
                                                                    <small>{[driver.node.phone, driver.node.dmvLicense, driver.node.branch?.name].filter(Boolean).join(' - ') || driver.node.email || ""}</small>
                                                                </p>
                                                            </Col>
                                                        </Row>
                                                    ))
                                                ) : (
                                                    <p className="p-2 mb-0 text-center text-muted">No drivers found.</p>
                                                )}
                                            </div>
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>
                        }
                        <Row>
                            <Col>
                                <FormGroup>
                                    <Label for="name">Person Name*</Label>
                                    <Controller
                                        name="name"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="text"
                                                placeholder="Enter person name"
                                                {...field}
                                                disabled={isDefaultVehicleDriver}
                                                invalid={!!errors.name}
                                            />
                                        )}
                                    />
                                    <FormFeedback>{errors.name?.message}</FormFeedback>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <Label for="phoneNumber">Phone Number</Label>
                                    <Controller
                                        name="phoneNumber"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="text"
                                                placeholder="Enter phone number"
                                                {...field}
                                                disabled={isDefaultVehicleDriver}
                                                invalid={!!errors.phoneNumber}
                                            />
                                        )}
                                    />
                                    <FormFeedback>{errors.phoneNumber?.message}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup>
                                    <Label for="email">Email</Label>
                                    <Controller
                                        name="email"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="email"
                                                placeholder="Enter email"
                                                {...field}
                                                disabled={isDefaultVehicleDriver}
                                                invalid={!!errors.email}
                                            />
                                        )}
                                    />
                                    <FormFeedback>{errors.email?.message}</FormFeedback>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <Label for="injuries">Injuries</Label>
                                    <Controller
                                        name="injuries"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                className="bos-custom-select"
                                                classNamePrefix="bos-select"
                                                {...field}
                                                options={injuryOptions}
                                                isLoading={injuriesLoading}
                                                isClearable
                                                placeholder="Select injuries"
                                                onChange={(option) => field.onChange(option ? option.value : '')}
                                                value={injuryOptions.find(opt => opt.value === field.value) || null}
                                            />
                                        )}
                                    />
                                    <FormFeedback>{errors.injuries?.message}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup>
                                    <Label for="driverLicense">Driver License</Label>
                                    <Controller
                                        name="driverLicense"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="text"
                                                placeholder="Enter driver license"
                                                {...field}
                                                disabled={isDefaultVehicleDriver}
                                                invalid={!!errors.driverLicense}
                                            />
                                        )}
                                    />
                                    <FormFeedback>{errors.driverLicense?.message}</FormFeedback>
                                </FormGroup>
                            </Col>
                        </Row>
                        <FormGroup>
                            <Label for="representative">Representative</Label>
                            <div className="d-flex align-items-end gap-2 representative-field-row">
                                <div className="flex-grow-1 representative-field-input">
                                    <Controller
                                        name="representativeId"
                                        control={control}
                                        render={({ field }) => (
                                            <RepresentativeSelect
                                                value={field.value}
                                                onChange={(optionValue) => field.onChange(optionValue)}
                                                extraOptions={createdRepresentativeOption ? [createdRepresentativeOption] : []}
                                            />
                                        )}
                                    />
                                </div>
                                <Button className="add-rep-btn" id="representativeAddTooltip" onClick={toggleRepresentativeModal}><i className="fa fa-plus" /></Button>
                                <UncontrolledTooltip target="representativeAddTooltip" placement="top">Add New Representative</UncontrolledTooltip>
                            </div>
                        </FormGroup>
                        <FormGroup>
                            <Label for="address">Address</Label>
                            <Controller
                                name="address"
                                control={control}
                                render={({ field }) => (
                                    isDefaultVehicleDriver ?
                                        <Input
                                            type="text"
                                            placeholder="Type to search for address"
                                            value={field.value ?? ""}
                                            disabled
                                            readOnly
                                        /> :
                                        <SearchPlaces
                                            value={field.value ?? ""}
                                            searchType="address"
                                            onSaveAction={(item) => field.onChange(item?.name ?? "")}
                                        />
                                )}
                            />
                            <FormFeedback>{errors.address?.message}</FormFeedback>
                        </FormGroup>

                        <FormGroup check className='mb-3'>
                            <Controller
                                name="isBusinessEntity"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        type="checkbox"
                                        id="isBusinessEntity"
                                        {...field}
                                        checked={field.value || false}
                                    />
                                )}
                            />
                            <Label for="isBusinessEntity" check className="ms-2">Is Business Entity</Label>
                            <FormFeedback>{errors.isBusinessEntity?.message}</FormFeedback>
                        </FormGroup>

                        <FormGroup>
                            <Label for="statement">Statement</Label>
                            <Controller
                                name="statement"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        type="textarea"
                                        placeholder="Enter statement"
                                        {...field}
                                        invalid={!!errors.statement}
                                    />
                                )}
                            />
                            <FormFeedback>{errors.statement?.message}</FormFeedback>
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={closeAndReset} disabled={isSubmitting}>Cancel</Button>
                        <Button color="primary" type="submit" disabled={isSubmitting}>{isSubmitting ? "Loading..." : "Add"}</Button>
                    </ModalFooter>
                </form>
            </Modal>
            {isRepresentativeModalOpen &&
                <AddRepresentativeModal
                    isOpen={isRepresentativeModalOpen}
                    toggle={toggleRepresentativeModal}
                    onCreate={handleNewRepresentative}
                />
            }
        </>
    )
}

export default AddPeopleModal
